/* eslint-disable jsdoc/check-param-names, jsdoc/require-param */
import {useCallback, useEffect, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {toggle} from 'opticks'

import {useBrand} from '../../_web/brand/hooks/useBrand'
import {setWhiteLabelUser} from '../modules/slice'
import {getUserInfoFromServer} from '../services'

const HP_LOGIN_BUTTON_ID = 'hp-login-button'

/**
 * Custom hook to handle HolidayPirates user info fetching and holiday pirates header profile image change detection
 *
 * **Warning:** This hook should only be used in FetchUser component. It creates MutationObservers that watch for
 * DOM changes and should not be instantiated multiple times to avoid duplicate observers and API calls.
 */
export const useHolidayPiratesUserInfo = () => {
  const dispatch = useDispatch()
  const {brandIsHolidayPirates} = useBrand()
  const shouldFetchUser = toggle('c0716159-white-label-user-info', false, true)
  const previousImageSrc = useRef<string | undefined>()

  const fetchUserInfo = useCallback(async () => {
    const userData = await getUserInfoFromServer()
    if (!userData) return

    dispatch(
      setWhiteLabelUser({
        ...userData
      })
    )
  }, [dispatch])

  useEffect(() => {
    if (brandIsHolidayPirates && shouldFetchUser) fetchUserInfo()
  }, [dispatch, brandIsHolidayPirates, shouldFetchUser])

  // Listen for changes to the hp-login-button image src attribute
  // When the image src changes from default to user's profile picture, it indicates login
  useEffect(() => {
    if (!brandIsHolidayPirates || !shouldFetchUser) return

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'src'
        ) {
          const target = mutation.target as HTMLImageElement

          // Check if this is the hp-login-button image
          if (target.id === HP_LOGIN_BUTTON_ID) {
            const currentSrc = target.src

            // If the image src has changed and is different from previous value, fetch user info
            if (currentSrc && currentSrc !== previousImageSrc.current) {
              previousImageSrc.current = currentSrc
              fetchUserInfo()
            }
          }
        }
      })
    })

    const observeLoginButton = (loginButton: Element) => {
      observer.observe(loginButton, {
        attributes: true,
        attributeFilter: ['src']
      })
    }

    const waitForLoginButtonAndObserve = () => {
      const bodyObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            let loginButton: Element | undefined

            for (let i = 0; i < mutation.addedNodes.length; i++) {
              const node = mutation.addedNodes[i]

              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element

                if (element.id === HP_LOGIN_BUTTON_ID) {
                  loginButton = element
                  break
                }

                const foundButton = element.querySelector(
                  `#${HP_LOGIN_BUTTON_ID}`
                )
                if (foundButton) {
                  loginButton = foundButton
                  break
                }
              }
            }

            if (loginButton) {
              // When the login button is found, switch to observing it directly
              const actualLoginButton =
                loginButton.id === HP_LOGIN_BUTTON_ID
                  ? loginButton
                  : loginButton.querySelector(`#${HP_LOGIN_BUTTON_ID}`)

              if (actualLoginButton) {
                bodyObserver.disconnect()
                observeLoginButton(actualLoginButton)

                // Check if image already has a src (user might already be logged in)
                const img = actualLoginButton as HTMLImageElement
                if (img.src && img.src !== previousImageSrc.current) {
                  previousImageSrc.current = img.src
                  fetchUserInfo()
                }
              }
            }
          }
        })
      })

      bodyObserver.observe(document.body, {
        childList: true,
        subtree: true
      })

      return bodyObserver
    }

    // Try to find the existing hp-login-button element first
    const existingLoginButton = document.getElementById(HP_LOGIN_BUTTON_ID)

    let bodyObserver: MutationObserver | null = null

    if (existingLoginButton) {
      observeLoginButton(existingLoginButton)

      // Check if image already has a src
      const img = existingLoginButton as HTMLImageElement
      if (img.src && img.src !== previousImageSrc.current) {
        previousImageSrc.current = img.src
        fetchUserInfo()
      }
    } else {
      bodyObserver = waitForLoginButtonAndObserve()
    }

    return () => {
      observer.disconnect()
      bodyObserver?.disconnect()
    }
  }, [dispatch, brandIsHolidayPirates, shouldFetchUser])
}
